/**
 * macOS / Windows dock-icon badge for chat unread count.
 *
 * Wraps the browser Badging API (`navigator.setAppBadge` /
 * `clearAppBadge`). The API ships in Chromium-based browsers
 * (Chrome / Edge / Brave) installed as PWAs, and in Safari for sites
 * added via Safari → File → Add to Dock. In a regular browser tab,
 * the call resolves but the badge doesn't render — silent no-op.
 *
 * Sums unread counts across every conversation visible in the
 * sidebar, including thread replies. The total is what shows on the
 * dock icon — Slack / Discord do the same.
 *
 * The hook re-applies the badge whenever the sum changes and clears
 * it on unmount. No-op in SSR / Node-test environments via the
 * `typeof navigator` guard.
 *
 * Browser quirks accommodated:
 *   - The API may throw on first invocation in some Safari versions
 *     (the user hasn't granted "Show in Dock" permissions yet). Wrap
 *     in try/catch and swallow — the app keeps working without the
 *     badge.
 *   - Some Chromium builds return a Promise that may reject.
 *     `.catch(() => undefined)` keeps console clean.
 *   - `setAppBadge(0)` and `clearAppBadge()` are equivalent per spec;
 *     we call `clearAppBadge()` for the 0 case so the dock icon
 *     visibly clears rather than showing a "0" pill.
 */

import { useEffect } from 'react'

import type { ChatConversationSummary } from '@/api/types-bridge'

interface BadgingNavigator {
  setAppBadge?: (count?: number) => Promise<void> | void
  clearAppBadge?: () => Promise<void> | void
}

function setBadge(count: number): void {
  if (typeof navigator === 'undefined') return
  const n = navigator as Navigator & BadgingNavigator
  try {
    if (count <= 0) {
      const result = n.clearAppBadge?.()
      // setAppBadge / clearAppBadge return Promises on most engines;
      // suppress unhandled rejections without leaking the failure to
      // the console. The badge silently won't render — acceptable.
      if (result && typeof (result as Promise<void>).catch === 'function') {
        ;(result as Promise<void>).catch(() => undefined)
      }
    } else {
      const result = n.setAppBadge?.(count)
      if (result && typeof (result as Promise<void>).catch === 'function') {
        ;(result as Promise<void>).catch(() => undefined)
      }
    }
  } catch {
    // Older Safari + some PWA contexts throw synchronously. Silent
    // fallback — the in-app unread badges still work.
  }
}

/**
 * Apply the dock-icon badge for the current total unread count.
 *
 * `conversations` may be undefined while the initial query is in
 * flight; in that case we leave the badge alone (don't clear it on
 * every cold-start refetch — the user just saw their counts the
 * moment before the page rerendered).
 *
 * On unmount, the badge is cleared so navigating away from 3mote
 * doesn't leave a stale pill on the dock icon for the rest of the
 * tab's lifecycle.
 */
export function useDockBadge(conversations: ChatConversationSummary[] | undefined): void {
  const total = conversations
    ? conversations.reduce(
        (sum, c) => sum + (c.unreadCount ?? 0) + (c.unreadThreadReplyCount ?? 0),
        0,
      )
    : null

  useEffect(() => {
    if (total === null) return
    setBadge(total)
  }, [total])

  // Always clear on unmount — separate effect so it fires once at
  // teardown regardless of the count-dependent effect above.
  useEffect(() => {
    return () => {
      setBadge(0)
    }
  }, [])
}
